import { isValidTagSlug } from '../../tags';
import type { Revision, RevisionTag, RevisionTags } from '../../types';
import { addUserEdits, isYValue, jsonToY, mutations } from '../../utils';

/**
 * Get a tag in a revision by its slug.
 */
export function getTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string
): mutations.MutableData<RevisionTag> | undefined {
    const tags = mutations.get(revision, 'tags');
    if (!tags) {
        return undefined;
    }
    return mutations.get(tags, slug);
}

/**
 * Insert a tag in a revision (keyed by slug).
 */
export function insertTagInRevision(
    revision: mutations.MutableData<Revision>,
    tag: RevisionTag
): void {
    // Ensure tags map exists
    let tags = mutations.get(revision, 'tags');
    if (!tags) {
        tags = (isYValue(revision) ? jsonToY({}) : {}) as mutations.MutableData<RevisionTags>;
        mutations.set(revision, 'tags', tags);
    }

    if (!isValidTagSlug(tag.slug)) {
        throw new Error('Invalid tag slug');
    }
    // Own keys only: a "constructor" tag must not collide with the plain object's prototype.
    const exists = isYValue(tags) ? mutations.has(tags, tag.slug) : Object.hasOwn(tags, tag.slug);
    if (exists) {
        throw new Error('Tag with same slug already exists in revision');
    }
    if (tag.parent !== undefined) {
        throw new Error('Set a tag parent with setTagParent, which checks the nesting rules');
    }
    if (tag.group !== undefined) {
        throw new Error('Set a tag category with setTagGroup, which moves its child tags along');
    }
    mutations.set(tags, tag.slug, tag);
}

/**
 * Remove a tag from a revision by slug.
 */
export function removeTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    authors: string[] = []
): void {
    const tags = mutations.get(revision, 'tags');
    if (tags) {
        mutations.del(tags, slug);
        // Its child tags stay, as top-level tags.
        for (const child of mutations.values(tags)) {
            if (mutations.get(child, 'parent') === slug) {
                mutations.del(child, 'parent');
                if (authors.length)
                    mutations.set(
                        child,
                        'edits',
                        addUserEdits(mutations.get(child, 'edits'), authors)
                    );
            }
        }
    }
}

/**
 * Update a tag in a revision by slug.
 */
export function updateTagInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    update: Partial<Omit<RevisionTag, 'slug'>>
): void {
    const existing = getTagInRevision(revision, slug);
    if (!existing) {
        throw new Error(`Tag doesn't exist in revision`);
    }
    if ('parent' in update) {
        throw new Error('Set a tag parent with setTagParent, which checks the nesting rules');
    }
    if ('group' in update) {
        throw new Error('Set a tag category with setTagGroup, which moves its child tags along');
    }
    Object.entries(update).forEach(([key, value]) => {
        mutations.set(
            existing,
            // @ts-ignore
            key,
            // @ts-ignore
            isYValue(revision) ? jsonToY(value) : value
        );
    });
}
